import { useEffect, useLayoutEffect, useRef } from 'react';
import { create } from 'zustand';
import { useAuth } from '../services/authentication/authContext';
import { supportService } from '../services/support/supportService';
import { useImpersonationStore } from './appImpersonationStore';

interface SupportState {
  identity: string | null;
  unreadCount: number;
  error: boolean;
  generation: number;
  setIdentity: (identity: string | null) => void;
  refreshUnread: () => Promise<void>;
}

let unreadRequest: AbortController | null = null;

/** Only customer badge state is shared. Transcripts stay local to their identity-keyed surface. */
export const useSupportStore = create<SupportState>((set, get) => ({
  identity: null, unreadCount: 0, error: false, generation: 0,
  setIdentity: identity => {
    if (identity !== get().identity) {
      unreadRequest?.abort();
      set(state => ({ identity, unreadCount: 0, error: false, generation: state.generation + 1 }));
    }
  },
  refreshUnread: async () => {
    const { identity, generation } = get();
    if (!identity) return;
    unreadRequest?.abort();
    const request = new AbortController();
    unreadRequest = request;
    try {
      const { count } = await supportService.unread(false, request.signal);
      if (!request.signal.aborted && get().identity === identity && get().generation === generation) set({ unreadCount: count, error: false });
    } catch (error) {
      if (request.signal.aborted) return;
      if (get().identity === identity && get().generation === generation) set({ error: true });
      throw error;
    }
  },
}));

export function useSupportIdentity(): string | null {
  const { user, backendUser, loading } = useAuth();
  const target = useImpersonationStore(state => state.target);
  const expectedId = target?.userId ?? user?.id;
  if (loading || !user || !backendUser || backendUser.id !== expectedId) return null;
  return `${user.id}:${backendUser.id}:${backendUser.created_by ?? backendUser.id}:${target ? 'impersonated' : 'direct'}`;
}

/** No background traffic; one in-flight refresh per subscriber and bounded failure backoff. */
export function useSupportRefresh(task: () => Promise<void>, interval: number, scope: string | null) {
  const latest = useRef(task);
  latest.current = task;
  useEffect(() => {
    if (!scope) return;
    let stopped = false;
    let busy = false;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      if (stopped || busy) return;
      clearTimeout(timer);
      if (document.visibilityState !== 'hidden' && navigator.onLine !== false) {
        busy = true;
        try { await latest.current(); failures = 0; }
        catch { failures = Math.min(failures + 1, 3); }
        finally { busy = false; }
      }
      if (!stopped) timer = setTimeout(tick, Math.min(interval * 2 ** failures, 300_000));
    };
    void tick();
    window.addEventListener('focus', tick);
    window.addEventListener('online', tick);
    document.addEventListener('visibilitychange', tick);
    return () => {
      stopped = true;
      clearTimeout(timer);
      window.removeEventListener('focus', tick);
      window.removeEventListener('online', tick);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [scope, interval]);
}

export function useSupportUnread(enabled: boolean): number {
  const identity = useSupportIdentity();
  const scope = enabled ? identity : null;
  const storedIdentity = useSupportStore(state => state.identity);
  const count = useSupportStore(state => state.unreadCount);
  useLayoutEffect(() => { useSupportStore.getState().setIdentity(scope); }, [scope]);
  useSupportRefresh(() => useSupportStore.getState().refreshUnread(), 60_000, scope);
  return scope && storedIdentity === scope ? count : 0;
}
